import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { GlobalSetting, UserResponse, Gender } from '../types/api';
import GlobalTimeslotManagement from './GlobalTimeslotManagement';
import BroadcastEmail from './BroadcastEmail';
import './Settings.css';

interface SettingsProps {
  settings: GlobalSetting[];
  isLoading: boolean;
  error: string;
  onSettingsUpdate: () => Promise<void>;
}

const Settings: React.FC<SettingsProps> = ({ settings, isLoading, error, onSettingsUpdate }) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saveLoading, setSaveLoading] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'settings' | 'timeslots' | 'broadcast' | 'statistics'>('settings');
  const [allClients, setAllClients] = useState<UserResponse[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'statistics') return;
    if (allClients.length > 0) return;
    setClientsLoading(true);
    apiService.getClients('').then(response => {
      if (response.data) {
        setAllClients(response.data);
      }
    }).catch(() => {
      // silently fail
    }).finally(() => {
      setClientsLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const activeClientsCount = useMemo(() => allClients.filter(c =>
    c.isActive &&
    c.subscription &&
    c.subscription.isActive &&
    new Date(c.subscription.actualEndDate || c.subscription.endDate) > new Date()
  ).length, [allClients]);

  const inactiveClientsCount = useMemo(() => allClients.filter(c =>
    !c.isActive ||
    !c.subscription ||
    !c.subscription.isActive ||
    new Date(c.subscription.actualEndDate || c.subscription.endDate) <= new Date()
  ).length, [allClients]);

  const handleEdit = (setting: GlobalSetting) => {
    setEditingId(setting.id);
    setEditValue(setting.value);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
    setSaveError('');
  };

  const handleSave = async (id: number) => {
    try {
      setSaveLoading(id);
      setSaveError('');

      const response = await apiService.updateSetting(id, { value: editValue });
      if (response.success) {
        setEditingId(null);
        setEditValue('');
        // Refresh settings from parent
        await onSettingsUpdate();
      } else {
        console.error('Failed to update setting');
        setSaveError(response.message || t('failedToUpdateSetting', 'Failed to update setting'));
      }
    } catch (err: any) {
      console.error('Error updating setting:', err);
      setSaveError(err.response?.data?.message || t('failedToUpdateSetting', 'Failed to update setting'));
    } finally {
      setSaveLoading(null);
    }
  };

  const getSettingDescription = (key: string) => {
    switch (key) {
      case 'GymCapacityPerTimeslot':
        return t('maxClientsPerTimeslot');
      case 'BookingCancellationDeadlineHours':
      case 'RegularClientCancellationDeadlineTime':
        return t('cancellationDeadlineRegular', 'Time when regular clients can no longer cancel appointments');
      case 'MorningClientCancellationDeadlineHours':
      case 'MorningClientCancellationDeadlineTime':
        return t('cancellationDeadlineMorning', 'Time when morning clients can no longer cancel appointments');
      case 'MorningAppointmentCutoffTime':
        return t('morningAppointmentCutoffTime', 'Time that defines when an appointment is considered a morning appointment');
      case 'MorningClientBookingCutoffTime':
        return t('morningClientBookingCutoffDescription', 'Morning clients can only book timeslots that start before this time');
      case 'AfternoonClientBookingStartTime':
        return t('afternoonClientBookingStartDescription', 'Afternoon clients can only book timeslots that start at or after this time');
      default:
        return t('globalSetting');
    }
  };

  const isTimeBasedSetting = (key: string) => {
    return key === 'BookingCancellationDeadlineHours' ||
           key === 'MorningClientCancellationDeadlineHours' ||
           key === 'MorningClientCancellationDeadlineTime' ||
           key === 'MorningAppointmentCutoffTime' ||
           key === 'MorningClientBookingCutoffTime' ||
           key === 'AfternoonClientBookingStartTime';
  };

  const getSettingDisplayName = (key: string) => {
    switch (key) {
      case 'GymCapacityPerTimeslot':
        return t('gymCapacityPerTimeslot');
      case 'BookingCancellationDeadlineHours':
      case 'RegularClientCancellationDeadlineTime':
        return t('regularClientCancellationDeadline');
      case 'MorningClientCancellationDeadlineHours':
      case 'MorningClientCancellationDeadlineTime':
        return t('morningClientCancellationDeadline');
      case 'MorningAppointmentCutoffTime':
        return t('morningAppointmentCutoff');
      case 'MorningClientBookingCutoffTime':
        return t('morningClientBookingCutoff');
      case 'AfternoonClientBookingStartTime':
        return t('afternoonClientBookingStart');
      default:
        return key;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading && activeTab === 'settings') {
    return (
      <div className="settings">
        <div className="loading">{t('loadingSettings')}</div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="settings-header">
        <h2>{t('adminSettings')}</h2>
        <p>{t('manageGymConfiguration')}</p>
        
        <div className="settings-tabs">
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            {t('globalSettings')}
          </button>
          <button
            className={`tab-button ${activeTab === 'timeslots' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeslots')}
          >
            {t('globalTimeslots')}
          </button>
          <button
            className={`tab-button ${activeTab === 'broadcast' ? 'active' : ''}`}
            onClick={() => setActiveTab('broadcast')}
          >
            {t('broadcastEmail')}
          </button>
          <button
            className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            {t('statistics')}
          </button>
        </div>
      </div>

      {(error || saveError) && activeTab === 'settings' && (
        <div className="error-message">
          {error || saveError}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="settings-grid">
          {settings.map((setting) => (
            <div key={setting.id} className="setting-card">
              <div className="setting-header">
                <h3>{getSettingDisplayName(setting.key)}</h3>
                <span className="setting-key">{setting.key}</span>
              </div>
              
              <p className="setting-description">
                {getSettingDescription(setting.key)}
              </p>

              <div className="setting-value-section">
                <label>{t('currentValue')}:</label>
                {editingId === setting.id ? (
                  <div className="edit-mode">
                    {isTimeBasedSetting(setting.key) ? (
                      <input
                        type="time"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="setting-input time-input"
                        autoFocus
                      />
                    ) : setting.key === 'RegularClientCancellationDeadlineTime' ? (
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="setting-input"
                        autoFocus
                        min="0"
                      />
                    ) : (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="setting-input"
                        autoFocus
                      />
                    )}
                    <div className="edit-actions">
                      <button
                        onClick={() => handleSave(setting.id)}
                        className="save-button"
                        disabled={saveLoading === setting.id}
                      >
                        {saveLoading === setting.id ? t('saving') : t('save')}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="cancel-button"
                        disabled={saveLoading === setting.id}
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="view-mode">
                    <span className="setting-value">
                      {setting.value}
                    </span>
                    <button
                      onClick={() => handleEdit(setting)}
                      className="edit-button"
                    >
                      {t('edit')}
                    </button>
                  </div>
                )}
              </div>

              <div className="setting-meta">
                <span className="last-updated">
                  {t('lastUpdated')}: {formatDate(setting.updatedAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'timeslots' && (
        <GlobalTimeslotManagement onUpdate={onSettingsUpdate} />
      )}

      {activeTab === 'broadcast' && (
        <BroadcastEmail onUpdate={onSettingsUpdate} />
      )}

      {activeTab === 'statistics' && (
        clientsLoading ? (
          <div className="loading">{t('loadingClients')}</div>
        ) : (
          <div className="stats-view">
            <section className="stats-section">
              <h2>{t('clientTotals')}</h2>
              <div className="stats-grid">
                <div className="stat-card readonly">
                  <h3>{t('totalClients')}</h3>
                  <p className="stat-number">{allClients.length}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('activeSubscriptions')}</h3>
                  <p className="stat-number">{activeClientsCount}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('inactiveClients')}</h3>
                  <p className="stat-number">{inactiveClientsCount}</p>
                </div>
              </div>
            </section>

            <section className="stats-section">
              <h2>{t('clientDemographics')}</h2>
              <div className="stats-grid">
                <div className="stat-card readonly">
                  <h3>{t('maleClients')}</h3>
                  <p className="stat-number">{allClients.filter(c => c.gender === Gender.Male).length}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('femaleClients')}</h3>
                  <p className="stat-number">{allClients.filter(c => c.gender === Gender.Female).length}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('genderNotSpecified')}</h3>
                  <p className="stat-number">{allClients.filter(c => c.gender == null).length}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('morningClients')}</h3>
                  <p className="stat-number">{allClients.filter(c => c.isMorningClient).length}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('afternoonClients')}</h3>
                  <p className="stat-number">{allClients.filter(c => c.isAfternoonClient).length}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('regularClients')}</h3>
                  <p className="stat-number">{allClients.filter(c => !c.isMorningClient && !c.isAfternoonClient).length}</p>
                </div>
                <div className="stat-card readonly">
                  <h3>{t('cardiologyProof')}</h3>
                  <p className="stat-number">{allClients.filter(c => c.cardiologyProof).length}</p>
                </div>
              </div>
            </section>
          </div>
        )
      )}
    </div>
  );
};

export default Settings; 